import { IAnalyticsEvent, useAnalytics } from "@dgs/core";
import { FC, MutableRefObject, ReactNode, createContext, useCallback, useEffect, useRef, useState } from "react";

interface Emitter {
	send(payload: string): void;
}

class NotLoggedInEmitter implements Emitter {
	send() {
		return;
	}
}

class NotYetConnectedEmitter implements Emitter {
	lastEvents: string[] = [];

	send(payload: string) {
		this.lastEvents = [...this.lastEvents, payload];
	}
}

export type PayloadMapper<T extends string> = (event: IAnalyticsEvent<T>) => Record<string, any> | null;

interface AnalyticsAdapterState {
	emitter: MutableRefObject<Emitter>;
}

const AnalyticsAdapterContext = createContext<AnalyticsAdapterState | undefined>(undefined);

interface Props<T extends string> {
	children: ReactNode;
	socketUrl: string;
	payloadMapper: PayloadMapper<T>;
}

export const DotnetAnalytics: FC<Props<any>> = ({ socketUrl, payloadMapper, children }) => {
	const [initialized, setInitialized] = useState(false);
	const emitter = useRef<Emitter>(new NotLoggedInEmitter());
	const { listen } = useAnalytics();

	useEffect(() => {
		//collect all tracking-events between now and a successful connection
		emitter.current = new NotYetConnectedEmitter();
		setInitialized(true);
		const socket = new WebSocket(socketUrl);
		socket.addEventListener("open", () => {
			if (emitter.current instanceof NotYetConnectedEmitter) {
				emitter.current.lastEvents.forEach((payload) => socket.send(payload));
			}
			emitter.current = socket;
		});
		return () => {
			emitter.current = new NotLoggedInEmitter();
			socket.close();
		};
	}, [socketUrl]);

	const handleEvent = useCallback(
		(event: IAnalyticsEvent<any>) => {
			console.log(event, emitter.current);
			const payload = payloadMapper(event);
			if (payload) {
				emitter.current.send(JSON.stringify({ event: event.type, payload }));
			}
		},
		[payloadMapper],
	);

	useEffect(() => listen(handleEvent), [listen, handleEvent]);

	return (
		<AnalyticsAdapterContext.Provider value={{ emitter: emitter }}>
			{initialized ? children : null}
		</AnalyticsAdapterContext.Provider>
	);
};
